/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { NodesPageTabs } from '@/components/NodesPageTabs/NodesPageTabs';
import { NodesPageTab } from '@/components/NodesPageTabs/types';
import { PageHeader } from '@/components/PageHeader';
import { usePaginatedNodesQuery } from '@/hooks/query/usePaginatedNodesQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Col } from '@/primitives/Col';
import { Row } from '@/primitives/Row';

export type FleetManagementHeaderProps = {
  fleetCount: number;
  fleetIsLoading: boolean;
};

export function FleetManagementHeader({ fleetCount, fleetIsLoading }: FleetManagementHeaderProps) {
  const { t } = useMimicTranslation('fleets');

  const configuredQuery = usePaginatedNodesQuery({
    params: { filters: { isOperating: true } },
    type: 'inMemory',
  });
  const pendingQuery = usePaginatedNodesQuery({
    params: { filters: { isOperating: false } },
    type: 'inMemory',
  });
  const archivedQuery = usePaginatedNodesQuery({
    params: { filters: { isArchived: true } },
    type: 'inMemory',
  });

  const counts = {
    configured: configuredQuery.data?.meta.page.totalItems ?? 0,
    pending: pendingQuery.data?.meta.page.totalItems ?? 0,
    archived: archivedQuery.data?.meta.page.totalItems ?? 0,
    fleets: fleetCount,
  };

  const loading = {
    configured: configuredQuery.isLoading,
    pending: pendingQuery.isLoading,
    archived: archivedQuery.isLoading,
    fleets: fleetIsLoading,
  };

  return (
    <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ margin: 0 }}>
      <Col xs={24} md={12} style={{ padding: 0 }}>
        <PageHeader title={t('title')} level={2} />
      </Col>
      <Col xs={24} md={12} xl={10} xxl={8} style={{ padding: 0 }}>
        <NodesPageTabs activeTab={NodesPageTab.FLEETS} counts={counts} loading={loading} />
      </Col>
    </Row>
  );
}
